import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { Copy, ExternalLink, RefreshCw } from 'lucide-react';

interface ReceiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiveAddress: string;
  /** Callback to check for new incoming transfers (e.g. after user sent from another device) */
  onCheckReceives?: () => void;
}

const QR_API = 'https://api.qrserver.com/v1/create-qr-code';
const QR_SIZE = 220;

/**
 * QR content: plain address only. Trust Wallet and most wallets recognize this when
 * the user is on the Send screen and scans — they get the recipient address filled in.
 * EIP-681 URLs are often not supported by Trust Wallet, so we use address-only for compatibility.
 */
export function ReceiveModal({
  open,
  onOpenChange,
  receiveAddress,
  onCheckReceives,
}: ReceiveModalProps) {
  const { toast } = useToast();

  const chainId = parseInt(process.env.REACT_APP_NETWORK_ID || '97', 10);
  const qrContent = receiveAddress.trim() || '';
  const qrSrc = qrContent
    ? `${QR_API}?size=${QR_SIZE}x${QR_SIZE}&data=${encodeURIComponent(qrContent)}`
    : '';

  const copyAddress = () => {
    if (!receiveAddress) return;
    navigator.clipboard.writeText(receiveAddress);
    toast({ title: 'Address copied to clipboard' });
  };

  const explorerUrl =
    chainId === 97
      ? `https://testnet.bscscan.com/address/${receiveAddress}`
      : `https://bscscan.com/address/${receiveAddress}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Receive tokens</DialogTitle>
        </DialogHeader>
        <div className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            Open your wallet (e.g. Trust Wallet), go to Send, then scan this QR to fill the
            recipient address. Select the token and amount in your wallet.
          </p>
          <p className='text-xs text-muted-foreground'>
            After they send, you’ll see a success notification and updated balance on the Wallets
            page within a few seconds.
          </p>
          {onCheckReceives && (
            <Button type='button' variant='secondary' className='w-full' onClick={onCheckReceives}>
              <RefreshCw className='w-4 h-4 mr-2' />
              {"I've sent – check for receive"}
            </Button>
          )}

          <div className='flex justify-center rounded-xl bg-muted/30 p-4 border border-border/50'>
            {qrSrc ? (
              <img
                src={qrSrc}
                alt='QR code: receive address'
                width={QR_SIZE}
                height={QR_SIZE}
                className='rounded-lg'
              />
            ) : (
              <div className='w-[220px] h-[220px] flex items-center justify-center text-muted-foreground text-sm'>
                No address
              </div>
            )}
          </div>

          <div className='space-y-2'>
            <Label>Receive address</Label>
            <div className='flex gap-2'>
              <Input
                value={receiveAddress}
                readOnly
                className='font-mono text-sm bg-muted/50 flex-1'
              />
              <Button
                type='button'
                variant='outline'
                size='icon'
                onClick={copyAddress}
                disabled={!receiveAddress}
              >
                <Copy className='w-4 h-4' />
              </Button>
              <Button
                type='button'
                variant='outline'
                size='icon'
                onClick={() => window.open(explorerUrl, '_blank')}
                disabled={!receiveAddress}
              >
                <ExternalLink className='w-4 h-4' />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
